import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllRoles } from '../../../utils/common/store/common.selectors';
import { fetchAllRoles } from '../../../utils/common/store/common.actions';
import { selectAuthUser } from '../../auth/store/auth.selectors';
import { fetchCollectionsByCompany } from '../../collection/store/collection.actions';
import { selectCollectionsByCompany } from '../../collection/store/collection.selectors';
import {
  addUserStatus,
  addUserError,
  selectEditUser,
  editUserStatus,
  editUserError,
  resetPwdError,
  resetPwdLoading,
} from '../store/user.selectors';
import { selectAllCompanies } from '../../company/store/company.selectors';
import {
  addUser,
  fetchAllUsers,
  resetSnackbarDetails,
  editUserFn,
  resetPassword,
} from '../store/user.actions';
import PropTypes from 'prop-types';
import InputAdornment from '@mui/material/InputAdornment';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Close,
  Business,
  Edit,
  WorkOutline,
  Email,
  Person,
  PersonOutline,
  BadgeOutlined,
  EmailOutlined,
} from '@mui/icons-material';
import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  StyledCloseButton,
  ModalContent,
  StyledTextField,
  StyledFormControl,
  StyledSelect,
  PrimaryButton,
  FormSection,
} from '../../../utils/common/styles/commonStyles';

const AddUserModal = ({ open, handleClose, initialCompanyID }) => {
  const dispatch = useDispatch();
  const roles = useSelector(selectAllRoles);
  const user = useSelector(selectAuthUser);
  const collections = useSelector(selectCollectionsByCompany);
  const newUsrStatus = useSelector(addUserStatus);
  const newUsrError = useSelector(addUserError);
  const editUsrStatus = useSelector(editUserStatus);
  const editUsrError = useSelector(editUserError);
  const companies = useSelector(selectAllCompanies);
  const editUser = useSelector(selectEditUser);
  const resetError = useSelector(resetPwdError);
  const resetLoading = useSelector(resetPwdLoading);
  const isUserAdmin = user?.rolename.includes('Rieker');

  // Derive company name from companies list based on initialCompanyID
  const selectedCompanyName = initialCompanyID
    ? companies?.find((c) => c.id === initialCompanyID)?.companyName
    : null;

  const initialFormData = {
    name: '',
    companyID: initialCompanyID || (isUserAdmin ? '' : user?.companyid),
    email: '',
    roleID: '',
    collectionID: '',
    isActive: false,
  };

  const [rolesOptions, setRolesOptions] = useState();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formData, setFormData] = useState(initialFormData);
  const [isEdit, setIsEdit] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(fetchAllRoles());
      // Fetch collections if company is pre-selected
      if (initialCompanyID) {
        dispatch(fetchCollectionsByCompany(initialCompanyID));
      }
    } else {
      setFormData(initialFormData);
      setIsEdit(false);
      setIsLoading(false);
    }
  }, [open, initialCompanyID, dispatch]);

  useEffect(() => {
    if (!isUserAdmin) {
      dispatch(fetchCollectionsByCompany(formData.companyID));
    }
  }, [formData.companyID, dispatch]);

  useEffect(() => {
    if (open && editUser) {
      setIsEdit(true);
      setFormData({
        name: editUser.name,
        email: editUser.email,
        companyID: editUser.companyID,
        roleID: editUser.roleID,
        isActive: editUser?.isActive,
      });
    }
  }, [open, editUser]);

  useEffect(() => {
    if (roles) {
      if (user.rolename === 'Supervisor')
        setRolesOptions(roles.filter((r) => r.roleName === 'User'));
      else if (user.rolename === 'Manager')
        setRolesOptions(roles.filter((r) => !r.roleName.includes('Rieker')));
      else setRolesOptions(roles);
    }
  }, [user, roles]);

  useEffect(() => {
    if (newUsrStatus === 'success' || editUsrStatus === 'success') {
      setSnackbar({
        open: true,
        message: isEdit ? 'User updated successfully!' : 'User created successfully!',
        severity: 'success',
      });
      dispatch(fetchAllUsers());
      handleClose();
      setFormData(initialFormData);
      setIsLoading(false);
    } else if (
      (newUsrStatus === 'fail' && newUsrError) ||
      (editUsrStatus === 'fail' && editUsrError)
    ) {
      setSnackbar({
        open: true,
        message: isEdit ? editUsrError : newUsrError,
        severity: 'error',
      });
    }
    dispatch(resetSnackbarDetails());
  }, [newUsrStatus, newUsrError, editUsrError, editUsrStatus]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'companyID') {
      dispatch(fetchCollectionsByCompany(value));
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (isEdit) dispatch(editUserFn({ ...formData, id: editUser.id }));
    else dispatch(addUser(formData));
  };

  const handleResetEmail = async () => {
    const result = await dispatch(resetPassword(editUser.id));
    if (result) {
      setSnackbar({
        open: true,
        message: 'Password reset email sent',
        severity: 'success',
      });
    } else {
      setSnackbar({
        open: true,
        message: resetError,
        severity: 'error',
      });
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false, message: '' }));
  };

  const isFormValid =
    formData.name &&
    formData.companyID &&
    formData.email &&
    formData.roleID &&
    (!(formData.roleID === 5 || formData.roleID === 4) || formData.collectionID);

  return (
    <>
      <StyledModal open={open} onClose={handleClose}>
        <Fade in={open} timeout={300}>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>
                {editUser ? <Edit /> : <Person />}
                {editUser ? 'Edit User' : 'Add New User'}
              </ModalTitle>
              <StyledCloseButton
                onClick={() => {
                  setFormData(initialFormData);
                  handleClose();
                }}
                size="small"
              >
                <Close />
              </StyledCloseButton>
            </ModalHeader>

            <ModalContent>
              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FormSection>
                  <StyledTextField
                    required
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter user name"
                    fullWidth
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutline color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormSection>

                <FormSection>
                  {initialCompanyID && selectedCompanyName ? (
                    <StyledTextField
                      label="Company Name"
                      value={selectedCompanyName}
                      disabled
                      fullWidth
                      size="small"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  ) : (
                    <StyledFormControl required fullWidth size="small">
                      <InputLabel>Company Name</InputLabel>
                      <StyledSelect
                        name="companyID"
                        value={formData.companyID}
                        onChange={handleChange}
                        label="Company Name"
                        disabled={editUser || !isUserAdmin}
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <Business color="action" fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        {companies?.map((company) => (
                          <MenuItem value={company.id} key={company.id}>
                            {company.companyName}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>
                  )}
                </FormSection>

                <FormSection>
                  <StyledTextField
                    required
                    label="Email"
                    name="email"
                    type="email"
                    disabled={editUser}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="user@company.com"
                    fullWidth
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormSection>

                <FormSection>
                  <StyledFormControl required fullWidth size="small">
                    <InputLabel>Role</InputLabel>
                    <StyledSelect
                      name="roleID"
                      value={formData.roleID}
                      onChange={handleChange}
                      label="Role"
                      startAdornment={
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                          <BadgeOutlined color="action" fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      {rolesOptions?.map((role) => (
                        <MenuItem value={role.id} key={role.id}>
                          {role.roleName}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </StyledFormControl>
                </FormSection>

                {(formData.roleID === 5 || formData.roleID === 4) && !editUser && (
                  <FormSection>
                    <StyledFormControl required fullWidth size="small">
                      <InputLabel>collection</InputLabel>
                      <StyledSelect
                        name="collectionID"
                        value={formData.collectionID}
                        onChange={handleChange}
                        label="collection"
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <WorkOutline color="action" fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        {collections?.map((collection) => (
                          <MenuItem value={collection.id} key={collection.id}>
                            {collection.name}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>
                  </FormSection>
                )}

                <FormSection>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive}
                        onChange={handleCheckboxChange}
                        sx={{
                          '&.Mui-checked': {
                            color: '#283048',
                          },
                        }}
                      />
                    }
                    label="Active"
                    sx={{
                      mt: -1,
                      ml: 0,
                    }}
                  />
                </FormSection>

                <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                  {editUser && (
                    <PrimaryButton onClick={handleResetEmail} startIcon={<EmailOutlined />}>
                      {resetLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={18} color="inherit" />
                          <span>Sending...</span>
                        </Box>
                      ) : (
                        <>Send Registration Mail</>
                      )}
                    </PrimaryButton>
                  )}
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={!isFormValid || isLoading}
                    startIcon={editUser ? <PersonOutline /> : <EmailOutlined />}
                  >
                    {isLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={18} color="inherit" />
                        <span>{editUser ? 'Updating...' : 'Creating...'}</span>
                      </Box>
                    ) : (
                      <>{editUser ? 'Update User' : 'Send Registration Mail'}</>
                    )}
                  </PrimaryButton>
                </Box>
              </Box>
            </ModalContent>
          </ModalContainer>
        </Fade>
      </StyledModal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddUserModal;

AddUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialCompanyID: PropTypes.number,
};
